import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { AdminNotification, AdminNotificationKind } from '../../core/models/admin-notification.models';
import { AuthService } from '../../core/services/auth.service';
import { AdminNotificationsService } from '../../core/services/admin-notifications.service';
import { UiPreferencesService } from '../../core/services/ui-preferences.service';
import { UserProfile } from '../../core/models/user.models';

@Component({
  selector: 'app-dashboard-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive],
  templateUrl: './dashboard-layout.component.html',
  styleUrl: './dashboard-layout.component.scss',
})
export class DashboardLayoutComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly uiPreferences = inject(UiPreferencesService);
  private readonly notificationsService = inject(AdminNotificationsService);
  private readonly subscriptions = new Subscription();

  user: UserProfile | null = null;
  loading = true;
  accessDenied = false;
  profileLoadError = '';
  sidebarCollapsed = false;
  mobileNavOpen = false;
  notificationsOpen = false;
  notifications: AdminNotification[] = [];
  unreadCount = 0;

  get canAccessReports(): boolean {
    return this.auth.hasAdminAccess(this.user);
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.uiPreferences.sidebarCollapsed$.subscribe((collapsed) => {
        this.sidebarCollapsed = collapsed;
      })
    );

    this.subscriptions.add(
      this.notificationsService.notifications$.subscribe((notifications) => {
        this.notifications = notifications;
      })
    );

    this.subscriptions.add(
      this.notificationsService.unreadCount$.subscribe((count) => {
        this.unreadCount = count;
      })
    );

    this.loadProfile();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.notificationsService.stopPolling();
  }

  toggleSidebar(): void {
    this.uiPreferences.toggleSidebar();
  }

  toggleMobileMenu(): void {
    this.mobileNavOpen = !this.mobileNavOpen;
  }

  closeMobileMenu(): void {
    this.mobileNavOpen = false;
  }

  toggleNotifications(): void {
    this.notificationsOpen = !this.notificationsOpen;
    if (this.notificationsOpen && this.unreadCount > 0) {
      this.notificationsService.markAllRead();
    }
  }

  openNotification(notification: AdminNotification): void {
    this.notificationsService.markAsRead(notification.id);
    this.notificationsOpen = false;
    void this.router.navigateByUrl(notification.route);
  }

  markAllNotificationsRead(): void {
    this.notificationsService.markAllRead();
  }

  retryProfileLoad(): void {
    this.loadProfile();
  }

  userDisplayName(): string {
    if (this.user?.member?.displayName) {
      return this.user.member.displayName;
    }
    const fullName = `${this.user?.member?.firstname ?? ''} ${this.user?.member?.lastname ?? ''}`.trim();
    if (fullName) {
      return fullName;
    }
    return this.user?.email ?? 'Utilisateur';
  }

  notificationTrackBy(_: number, item: AdminNotification): string {
    return item.id;
  }

  notificationTime(notification: AdminNotification): string {
    const date = this.parseNotificationDate(notification.createdAt);
    if (!date) {
      return "A l'instant";
    }

    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  }

  notificationKindIcon(kind: AdminNotificationKind): string {
    switch (kind) {
      case 'recipe_review':
        return '🍲';
      case 'inspiration_review':
        return '💡';
      case 'comment_review':
        return '💬';
      case 'report_open':
        return '🚩';
      default:
        return '🔔';
    }
  }

  notificationKindLabel(kind: AdminNotificationKind): string {
    switch (kind) {
      case 'recipe_review':
        return 'Recette';
      case 'inspiration_review':
        return 'Inspiration';
      case 'comment_review':
        return "Message d'avis";
      case 'report_open':
        return 'Signalement';
      default:
        return 'Notification';
    }
  }

  logout(): void {
    this.auth.logout();
    this.router.navigate(['/login']);
  }

  private loadProfile(): void {
    this.loading = true;
    this.profileLoadError = '';

    this.auth.refreshProfile().subscribe({
      next: (profile) => {
        this.loading = false;
        this.user = profile;

        if (!profile) {
          this.notificationsService.stopPolling();
          this.router.navigate(['/login']);
          return;
        }

        this.accessDenied = !this.auth.hasAdminAccess(profile);
        if (this.accessDenied) {
          this.notificationsService.stopPolling();
          this.auth.logout();
          this.router.navigate(['/login']);
          return;
        }

        this.notificationsService.startPolling(this.canAccessReports);
      },
      error: () => {
        this.loading = false;
        this.notificationsService.stopPolling();
        this.profileLoadError = 'Impossible de charger le profil. Vérifie la connexion puis réessaie.';
      },
    });
  }

  private parseNotificationDate(value: string | number | undefined): Date | null {
    if (value == null) {
      return null;
    }

    if (typeof value === 'number') {
      const timestamp = value > 1_000_000_000_000 ? value : value * 1000;
      const date = new Date(timestamp);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const normalized = value.trim();
    if (!normalized) {
      return null;
    }

    if (/^\d+$/.test(normalized)) {
      const numeric = Number.parseInt(normalized, 10);
      if (Number.isFinite(numeric)) {
        const timestamp = numeric > 1_000_000_000_000 ? numeric : numeric * 1000;
        const date = new Date(timestamp);
        return Number.isNaN(date.getTime()) ? null : date;
      }
      return null;
    }

    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  }
}
